import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCirclePlay,
  faCirclePause,
  faVolumeUp,
  faVolumeMute,
  faForward,
  faBackward,
  faZap
} from '@fortawesome/free-solid-svg-icons';
import * as noUiSlider from 'nouislider';

// Extender el tipo HTMLElement para noUiSlider
interface NoUiSliderElement extends HTMLElement {
  noUiSlider?: noUiSlider.API;
}

interface CustomAudioControlProps {
  audioSrc: string;
  onTimeUpdate: (currentTime: number) => void;
  audioRef: React.RefObject<HTMLAudioElement>;
}

const CustomAudioControl: React.FC<CustomAudioControlProps> = ({
  audioSrc,
  onTimeUpdate,
  audioRef
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.75);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);

  // Referencias para los sliders
  const progressSliderRef = useRef<HTMLDivElement>(null);
  const volumeSliderRef = useRef<HTMLDivElement>(null);

  // Referencias a las instancias de los sliders
  const progressSliderInstance = useRef<noUiSlider.API | null>(null);
  const volumeSliderInstance = useRef<noUiSlider.API | null>(null);

  // Inicializar el slider de progreso
  useEffect(() => {
    if (!progressSliderRef.current) return;

    const initProgressSlider = () => {
      const progressSliderElement =
        progressSliderRef.current as NoUiSliderElement;

      // Si ya existe un slider, destruirlo
      if (progressSliderElement.noUiSlider) {
        progressSliderElement.noUiSlider.destroy();
      }

      // Crear el nuevo slider
      const slider = noUiSlider.create(progressSliderElement, {
        start: [0],
        range: {
          min: 0,
          max: 100
        },
        connect: [true, false],
        step: 0.01, // Más granular para mayor precisión
        behaviour: 'snap-drag' // Respuesta más inmediata
      });

      progressSliderInstance.current = slider;

      // Eventos del slider
      slider.on('start', () => {
        setIsDraggingProgress(true);
      });

      slider.on('slide', values => {
        if (audioRef.current && duration > 0) {
          const newTime = (parseFloat(values[0] as string) / 100) * duration;
          audioRef.current.currentTime = newTime;

          // Actualizar la etiqueta de tiempo mientras se desliza
          setCurrentTime(newTime);
        }
      });

      slider.on('end', () => {
        setIsDraggingProgress(false);
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
      });
    };

    initProgressSlider();

    return () => {
      if (progressSliderInstance.current) {
        progressSliderInstance.current.destroy();
        progressSliderInstance.current = null;
      }
    };
  }, [duration, audioRef]);

  // Inicializar el slider de volumen
  useEffect(() => {
    if (!volumeSliderRef.current) return;

    const initVolumeSlider = () => {
      const volumeSliderElement = volumeSliderRef.current as NoUiSliderElement;

      // Si ya existe un slider, destruirlo
      if (volumeSliderElement.noUiSlider) {
        volumeSliderElement.noUiSlider.destroy();
      }

      // Crear el nuevo slider
      const slider = noUiSlider.create(volumeSliderElement, {
        start: [volume * 100],
        range: {
          min: 0,
          max: 100
        },
        connect: [true, false],
        step: 1, // Incrementos más finos
        behaviour: 'tap-drag', // Comportamiento más responsivo a clics
        animate: false // Sin animaciones para mayor fluidez
      });

      volumeSliderInstance.current = slider;

      // Eventos del slider
      const updateVolume = (values: (string | number)[]) => {
        const newVolume = parseFloat(values[0] as string) / 100;
        if (audioRef.current) {
          audioRef.current.volume = newVolume;
        }
      };

      // Capturar todos los eventos de interacción
      slider.on('update', updateVolume);
      slider.on('set', values => {
        const newVolume = parseFloat(values[0] as string) / 100;
        setVolume(newVolume);
      });

      // Capturar los clicks directos en la barra
      volumeSliderElement.addEventListener('click', () => {
        if (volumeSliderInstance.current) {
          const newValue = volumeSliderInstance.current.get() as string;
          const newVolume = parseFloat(newValue) / 100;
          if (audioRef.current) {
            audioRef.current.volume = newVolume;
            setVolume(newVolume);
          }
        }
      });
    };

    initVolumeSlider();

    return () => {
      if (volumeSliderInstance.current) {
        volumeSliderRef.current?.removeEventListener('click', () => {});
        volumeSliderInstance.current.destroy();
        volumeSliderInstance.current = null;
      }
    };
  }, [audioRef, volume]);

  // Actualizar el slider de volumen cuando cambia el estado de volumen
  useEffect(() => {
    // Solo actualizar el slider si hay un cambio significativo de estado
    // para evitar bucles de actualización
    if (volumeSliderInstance.current && volumeSliderRef.current) {
      const currentValue =
        parseFloat(volumeSliderInstance.current.get() as string) / 100;
      if (Math.abs(currentValue - volume) > 0.01) {
        volumeSliderInstance.current.set([volume * 100]);
      }
    }
  }, [volume]);

  // Manejar eventos de audio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
      setCurrentTime(0);
      if (progressSliderInstance.current) {
        progressSliderInstance.current.set([0]);
      }
    };

    const handleTimeUpdate = () => {
      if (!isDraggingProgress) {
        const audio = audioRef.current;
        if (!audio) return;

        setCurrentTime(audio.currentTime);
        const progress = audio.duration
          ? (audio.currentTime / audio.duration) * 100
          : 0;

        // Usar requestAnimationFrame para actualizar el slider de forma más eficiente
        requestAnimationFrame(() => {
          if (progressSliderInstance.current && !isDraggingProgress) {
            progressSliderInstance.current.set([progress]);
          }
        });

        onTimeUpdate(audio.currentTime);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (progressSliderInstance.current) {
        progressSliderInstance.current.set([0]);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioRef, onTimeUpdate, isDraggingProgress]);

  // Resetear cuando cambia el src del audio
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);

    if (progressSliderInstance.current) {
      progressSliderInstance.current.set([0]);
    }

    // Si el audio ya está cargado, configurar la duración
    if (audioRef.current && audioRef.current.duration) {
      setDuration(audioRef.current.duration);
    }
  }, [audioSrc]);

  // Controles de reproducción
  const togglePlay = () => {
    if (!audioRef.current) return;
    isPlaying ? audioRef.current.pause() : audioRef.current.play();
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        audioRef.current.currentTime + 10,
        duration
      );
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(
        audioRef.current.currentTime - 10,
        0
      );
    }
  };

  // Formatear tiempo
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0'
    )}`;
  };

  // Cambiar velocidad de reproducción
  const togglePlaybackRate = () => {
    const rates = [1, 1.3, 1.6, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const newRate = rates[(currentIndex + 1) % rates.length];
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
    setPlaybackRate(newRate);
  };

  // Manejar cambio de volumen con click en icono
  const toggleMute = () => {
    if (audioRef.current) {
      const newVolume = volume > 0 ? 0 : 0.75;
      audioRef.current.volume = newVolume;
      setVolume(newVolume);

      // Actualizar el slider
      if (volumeSliderInstance.current) {
        volumeSliderInstance.current.set([newVolume * 100]);
      }
    }
  };

  return (
    <div className="custom-audio-player fs-9 p-0">
      <audio ref={audioRef} src={audioSrc} preload="auto" />
      <div className="controls">
        <div className="time-controls">
          <button
            onClick={skipBackward}
            className="m-0 text-primary fs-9"
            aria-label="Rewind 10 seconds"
            type="button"
          >
            <FontAwesomeIcon icon={faBackward} />
          </button>
          <button
            onClick={togglePlay}
            className="m-0 p-0 text-primary fs-6"
            aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
            type="button"
          >
            <FontAwesomeIcon icon={isPlaying ? faCirclePause : faCirclePlay} />
          </button>
          <button
            onClick={skipForward}
            className="m-0 text-primary fs-9"
            aria-label="Forward 10 seconds"
            type="button"
          >
            <FontAwesomeIcon icon={faForward} />
          </button>
          <button
            onClick={togglePlaybackRate}
            className="m-0 text-primary fs-10 pb-1"
            aria-label={`Toggle playback speed: currently ${playbackRate}x`}
            type="button"
          >
            <span className="fs-11 p-0 m-0">{playbackRate}x</span> <br />
            <FontAwesomeIcon icon={faZap} />
          </button>
          <span>{formatTime(currentTime)}</span>
          <div
            className="noUi-primary-lighter noUi-handle-primary2 noUi-slider-slim2 noUi-handle-circle2"
            ref={progressSliderRef}
            style={{ width: '150px', margin: '0 10px' }}
          ></div>
          <span>{formatTime(duration)}</span>
          <span className="volume-control">
            <FontAwesomeIcon
              icon={volume > 0.01 ? faVolumeUp : faVolumeMute}
              onClick={toggleMute}
              className="cursor-pointer"
              style={{ cursor: 'pointer' }}
            />
            <div
              className="noUi-primary-lighter noUi-handle-primary2 noUi-slider-slim2 noUi-handle-circle2 volume-slider"
              ref={volumeSliderRef}
              style={{ width: '50px', marginLeft: '10px', cursor: 'pointer' }}
            ></div>
          </span>
        </div>
      </div>
    </div>
  );
};

export default CustomAudioControl;
